import {makeAutoObservable} from 'mobx';
import witTrainingQueueService from "../services/witTrainingQueueService";
import witTrainingApiService from "../services/witTrainingApiService";
import {createWitTrainingKey, hashWitText, normalizeWitText} from "../utils/witText";

const normalizeFilterValue = (value) => (value && value !== 'all' ? value : 'all');

export class WitTrainingQueueState {
  rootStore;
  entries = [];
  dataLoaded = false;
  statusFilter = 'all';
  searchText = '';
  savingIds = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setEntries(entries) {
    this.entries = Array.isArray(entries) ? entries : [];
  }

  setStatusFilter(value) {
    this.statusFilter = normalizeFilterValue(value);
  }

  setSearchText(value) {
    this.searchText = value || '';
  }

  getStatusOptions() {
    return [...new Set(this.entries.map((entry) => entry.status).filter(Boolean))].sort();
  }

  getFilteredEntries() {
    const searchNeedle = this.searchText.trim().toLowerCase();
    return this.entries.filter((entry) => {
      if (this.statusFilter !== 'all' && (entry.status || 'pending') !== this.statusFilter) {
        return false;
      }
      if (searchNeedle) {
        const haystack = [entry.text, entry.intent, entry.entityType, entry.entityValue, entry.sourceAuditId, entry.status, entry.error]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(searchNeedle)) {
          return false;
        }
      }
      return true;
    });
  }

  getCounts() {
    return {
      total: this.entries.length,
      pending: this.entries.filter((entry) => (entry.status || 'pending') === 'pending').length,
      sending: this.entries.filter((entry) => entry.status === 'sending').length,
      trained: this.entries.filter((entry) => entry.status === 'trained').length,
      failed: this.entries.filter((entry) => entry.status === 'failed').length,
      visiblePending: this.getVisiblePendingEntries().length,
      visibleFailed: this.getVisibleFailedEntries().length,
      duplicates: this.entries.filter((entry) => (entry.duplicateCount || 1) > 1).length,
    };
  }

  getVisiblePendingEntries() {
    return this.getFilteredEntries().filter((entry) => (entry.status || 'pending') === 'pending');
  }

  getVisibleFailedEntries() {
    return this.getFilteredEntries().filter((entry) => entry.status === 'failed');
  }

  isSaving(entryId) {
    return this.savingIds.includes(entryId);
  }

  loadQueueEntries() {
    return witTrainingQueueService.loadQueueEntries()
      .then((entries) => {
        this.setEntries(entries);
        this.dataLoaded = true;
      })
      .catch((reason) => {
        this.dataLoaded = true;
        console.error(reason);
      });
  }

  clearQueueEntries() {
    return witTrainingQueueService.clearQueueEntries()
      .then(() => {
        this.setEntries([]);
      })
      .catch(console.error);
  }

  saveQueueEntry(queueEntry) {
    if (!queueEntry?.id) {
      return Promise.resolve();
    }
    this.savingIds = [...this.savingIds, queueEntry.id];
    return witTrainingQueueService.saveQueueEntry(queueEntry.id, queueEntry)
      .then(() => {
        const nextEntries = [queueEntry, ...this.entries.filter((entry) => entry.id !== queueEntry.id)];
        this.setEntries(nextEntries);
      })
      .catch(console.error)
      .finally(() => {
        this.savingIds = this.savingIds.filter((id) => id !== queueEntry.id);
      });
  }

  async createManualQueueEntry({text, intent, trainingKind = 'intent', entityType = '', entityValue = '', sendNow = false}) {
    const cleanText = (text || '').trim().substring(0, 280);
    const cleanIntent = (intent || '').trim();
    const cleanTrainingKind = trainingKind === 'entity' ? 'entity' : 'intent';
    const cleanEntityType = (entityType || '').trim();
    const cleanEntityValue = (entityValue || '').trim();
    const isValidIntentEntry = cleanTrainingKind === 'intent' && cleanText && cleanIntent;
    const isValidEntityEntry = cleanTrainingKind === 'entity' && cleanText && cleanEntityType && cleanEntityValue && cleanText.includes(cleanEntityValue);

    if (!isValidIntentEntry && !isValidEntityEntry) {
      return null;
    }

    const label = cleanTrainingKind === 'entity' ? cleanEntityType : cleanIntent;
    const entryId = createWitTrainingKey(cleanText, label, cleanTrainingKind, cleanEntityValue);
    const now = new Date().toISOString();
    const existingEntry = this.entries.find((entry) => entry.id === entryId);
    const queueEntry = {
      id: entryId,
      text: cleanText,
      normalizedText: normalizeWitText(cleanText),
      textHash: hashWitText(cleanText),
      trainingKind: cleanTrainingKind,
      intent: cleanTrainingKind === 'intent' ? cleanIntent : 'none',
      entityType: cleanTrainingKind === 'entity' ? cleanEntityType : null,
      entityValue: cleanTrainingKind === 'entity' ? cleanEntityValue : null,
      sourceAuditId: existingEntry?.sourceAuditId || 'manual',
      duplicateSourceAuditIds: existingEntry?.duplicateSourceAuditIds || [],
      duplicateCount: existingEntry?.duplicateCount || 1,
      actionKey: existingEntry?.actionKey || 'manual',
      currentIntent: existingEntry?.currentIntent || null,
      responseIntent: existingEntry?.responseIntent || null,
      responseConfidence: existingEntry?.responseConfidence ?? null,
      decision: existingEntry?.decision || null,
      status: 'pending',
      error: null,
      createdAt: existingEntry?.createdAt || now,
      updatedAt: now,
      queuedBy: this.rootStore?.authState?.user?.uid || null,
      lastQueuedFromAuditId: existingEntry?.lastQueuedFromAuditId || null,
      manual: true,
    };

    await this.saveQueueEntry(queueEntry);
    if (sendNow) {
      await this.sendEntryToWit(entryId);
    }
    return entryId;
  }

  updateQueueStatus(entryId, status) {
    const patch = {
      status,
      error: null,
      updatedAt: new Date().toISOString(),
    };
    this.savingIds = [...this.savingIds, entryId];
    return witTrainingQueueService.updateQueueEntry(entryId, patch)
      .then(() => {
        this.entries = this.entries.map((entry) => entry.id === entryId ? {...entry, ...patch} : entry);
      })
      .catch(console.error)
      .finally(() => {
        this.savingIds = this.savingIds.filter((id) => id !== entryId);
      });
  }

  async sendEntryToWit(entryId) {
    const entry = this.entries.find((item) => item.id === entryId);
    const isIntentEntry = (entry?.trainingKind || 'intent') === 'intent';
    const isValidIntentEntry = isIntentEntry && entry?.intent && entry?.text;
    const isValidEntityEntry = !isIntentEntry && entry?.entityType && entry?.entityValue && entry?.text;
    if (!entry || (!isValidIntentEntry && !isValidEntityEntry)) {
      return;
    }

    const sendingPatch = {
      status: 'sending',
      error: null,
      updatedAt: new Date().toISOString(),
    };

    this.savingIds = [...this.savingIds, entryId];
    this.entries = this.entries.map((item) => item.id === entryId ? {...item, ...sendingPatch} : item);

    try {
      await witTrainingQueueService.updateQueueEntry(entryId, sendingPatch);
      const authToken = this.rootStore?.authState?.serverToken;
      if (!authToken) {
        throw new Error('missing_auth_token');
      }
      const response = await witTrainingApiService.sendQueueEntry(entry, authToken);
      const successPatch = {
        status: 'trained',
        error: null,
        sentAt: new Date().toISOString(),
        witResponse: response,
        updatedAt: new Date().toISOString(),
      };
      await witTrainingQueueService.updateQueueEntry(entryId, successPatch);
      this.entries = this.entries.map((item) => item.id === entryId ? {...item, ...successPatch} : item);
    } catch (error) {
      const responseError = error?.response?.data?.error || null;
      const payloadPreview = error?.response?.data?.witPayloadPreview || null;
      const detailedError = payloadPreview
        ? `${responseError || error?.message || 'unknown_error'} | payload=${JSON.stringify(payloadPreview)}`
        : (responseError || error?.message || 'unknown_error');
      const failedPatch = {
        status: 'failed',
        error: detailedError,
        updatedAt: new Date().toISOString(),
      };
      await witTrainingQueueService.updateQueueEntry(entryId, failedPatch);
      this.entries = this.entries.map((item) => item.id === entryId ? {...item, ...failedPatch} : item);
      console.error(error);
    } finally {
      this.savingIds = this.savingIds.filter((id) => id !== entryId);
    }
  }

  async sendVisiblePendingEntries() {
    const entries = this.getVisiblePendingEntries();
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      await this.sendEntryToWit(entry.id);
    }
  }

  async retryVisibleFailedEntries() {
    const entries = this.getVisibleFailedEntries();
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      await this.sendEntryToWit(entry.id);
    }
  }

  exportEntriesText(entries = this.getFilteredEntries()) {
    return JSON.stringify(entries, null, 2);
  }
}
